import { IP_API_URL, LOCATION_INFO_STORAGE_KEY } from '../../constants'
import { useState, useEffect } from 'react'
import { fetch } from '../../utils'
import { IPField, IPResponse } from './types'
import useBrowserStorage from '../useBrowserStorage'

/**
 * useIPLookup uses https://ipapi.co/json/ to get the user's IP address and location information.
 * It then returns an object with the fields requested.
 * @param fields a set of fields to return, based on the enum RequiredFields
 * @return an object with the fields requested
 */
const useIPLookup = (requestedFields?: IPField[]) => {
  const [ipInformation, setIPInformation] = useState<IPResponse>({})
  const [locationInfo, setLocationInfo] = useBrowserStorage<{
    ipInformation: IPResponse
    lastUpdated: number
  }>(LOCATION_INFO_STORAGE_KEY, 'local', {
    ipInformation: {},
    lastUpdated: 0
  })

  useEffect(() => {
    const fetchIPInformation = async () => {
      try {
        // If we have the location information in the browser storage, use it
        if (locationInfo?.ipInformation && locationInfo?.lastUpdated) {
          const currentTime = Date.now()
          // If the location information is less than 24 hours old, use it
          if (currentTime - locationInfo.lastUpdated < 24 * 60 * 60 * 1000) {
            setIPInformation(locationInfo.ipInformation)
            return
          }
        }
        const response = await fetch(IP_API_URL) // 30k free requests per month (~1k a day) - at scale, we'd need to pay, or find another service
        setIPInformation(response)
        // Store the IP information in the browser storage
        setLocationInfo({
          ipInformation: response,
          lastUpdated: Date.now()
        })
      } catch {
        // fail silently, and set the IP information to an empty object
        setIPInformation({})
      }
    }
    fetchIPInformation()
  }, [locationInfo, setLocationInfo])

  const filterIPInformation = (ipInformation: IPResponse, requestedFields: IPField[]) => {
    const deduplicatedFields = requestedFields.filter(
      (field, index) => requestedFields.indexOf(field) === index
    )

    Object.keys(ipInformation).forEach((key: string) => {
      if (!deduplicatedFields.includes(key as IPField)) {
        delete ipInformation[key as IPField]
      }
    })

    return ipInformation
  }

  if (requestedFields) {
    return filterIPInformation(ipInformation, requestedFields)
  }

  return ipInformation
}

export default useIPLookup
